import { useState, useContext, createContext, useEffect } from 'react'

// Storage key for view mode persistence
const VIEW_MODE_STORAGE_KEY = 'kidsParentsApp_viewMode'

// Helper functions for localStorage
const saveViewMode = (mode) => {
  try {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode)
  } catch (error) {
    console.warn('Failed to save view mode:', error)
  }
}

const loadViewMode = () => {
  try {
    const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    return saved || 'activity' // Default to 'activity' if nothing saved
  } catch (error) {
    console.warn('Failed to load view mode:', error)
    return 'activity'
  }
}

// Create context for view mode management
const ViewModeContext = createContext()

// Provider component for view mode context
export const ViewModeProvider = ({ children }) => {
  const [viewMode, setViewMode] = useState(() => loadViewMode()) // Initialize from localStorage
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false)

  // Save to localStorage whenever view mode changes
  useEffect(() => {
    saveViewMode(viewMode)
  }, [viewMode])

  const toggleViewMode = () => {
    // Cycle through: student -> activity -> date -> student
    setViewMode(prevMode => {
      if (prevMode === 'student') return 'activity';
      if (prevMode === 'activity') return 'date';
      return 'student';
    });
  }

  const toggleSettingsPanel = () => {
    setSettingsPanelOpen(prev => !prev)
  }

  const value = {
    viewMode,
    setViewMode,
    toggleViewMode,
    settingsPanelOpen,
    setSettingsPanelOpen,
    toggleSettingsPanel
  }

  return (
    <ViewModeContext.Provider value={value}>
      {children}
    </ViewModeContext.Provider>
  )
}

// Custom hook to use view mode context
export const useViewMode = () => {
  const context = useContext(ViewModeContext)
  if (!context) {
    throw new Error('useViewMode must be used within a ViewModeProvider')
  }
  return context
}

// Alternative standalone hook for simple use cases
export const useSimpleViewMode = (initialMode = 'student') => {
  const [viewMode, setViewMode] = useState(initialMode)
  
  const toggleViewMode = () => {
    setViewMode(prevMode => prevMode === 'student' ? 'activity' : 'student')
  }

  return {
    viewMode,
    setViewMode,
    toggleViewMode
  }
}